/**
 * CR500 Phase 2F: DealCardRendererV2
 * CR500 Phase 6A: Telemetry integration
 * CR309: Central SSOT guard for ribbon visibility
 * 
 * Block-based card renderer using DealCardSpec v2.
 * Feature-flag gated - only active when dealCardSpecV2_enabled is true.
 * 
 * Phase 2F enhancements:
 * - Placeholder blocks only render with ?dealCardV2debug=1
 * - Spec coverage audit for debugging
 */

import React, { useMemo, useEffect, useRef } from "react";
import type { Deal } from "@/lib/types/deals";
import type { 
  ResolvedDealCardSpecV2, 
  DealCardBlockSpec,
  DealCardRenderContext,
  DealCardSurfaceKey,
} from "@/lib/deal-cards/specV2/types";
import { getEnabledBlocks, computeSpecFingerprint } from "@/lib/deal-cards/specV2/resolveSpec";
import { getBlockComponent, isBlockExtracted } from "@/lib/deal-cards/blocks/registry";
import { isV2DebugEnabled } from "@/lib/deal-cards/helpers";
import { trackRenderEvent } from "@/lib/deal-cards/telemetry";
import { cn } from "@/lib/utils";
import { DealIdBadge } from "@/components/deals/DealIdBadge";

// CR305: Debug render signature (safe iframe detection)
import { 
  shouldRenderDealCardSignatureStrip, 
  buildDealCardSignature 
} from "@/lib/deals/debugDealCardSignature";

// ================================================================
// TYPES
// ================================================================

export interface DealCardRendererV2Props {
  /** The deal to render */
  deal: Deal & { id?: string; dedupe_key?: string };
  /** The resolved spec for this deal type */
  spec: ResolvedDealCardSpecV2;
  /** Is this an admin context? */
  isAdmin?: boolean;
  /** Which surface to render (primary = front, details = back) */
  surface?: DealCardSurfaceKey;
  /** Optional class name for the container */
  className?: string;
  /** Optional style overrides */
  style?: React.CSSProperties;
  /** Enable debug mode (shows block IDs) - auto-enabled with ?dealCardV2debug=1 */
  debug?: boolean;
  /** Optional block IDs to suppress at render time */
  suppressBlockIds?: string[];
}

// Debug mode uses centralized guard from helpers/debugGuard.ts

// ================================================================
// COMPONENT
// ================================================================

export function DealCardRendererV2({
  deal,
  spec,
  isAdmin = false,
  surface = "primary",
  className,
  style,
  debug: debugProp = false,
  suppressBlockIds,
}: DealCardRendererV2Props) {
  // Auto-enable debug via centralized query param guard
  const debug = debugProp || isV2DebugEnabled();
  
  // Get enabled blocks for the requested surface, filtering out placeholders unless debug
  const enabledBlocks = useMemo(() => {
    const blocks = getEnabledBlocks(spec, surface).filter((block) => {
      if (!suppressBlockIds || suppressBlockIds.length === 0) return true;
      return !suppressBlockIds.includes(block.id);
    });
    // CR309: Central guard - enforce ribbon SSOT even if stray path tries to render
    // Belt-and-suspenders: explicitly filter out ribbon if block.enabled !== true
    const safeBlocks = blocks.filter(block => {
      // CR309: Ribbon requires explicit enabled=true from blockToggles
      if (block.id === 'ribbon' && block.enabled !== true) {
        if (debug) {
          console.debug("[DealCardRendererV2] CR309 guard: ribbon blocked (enabled !== true)", {
            blockEnabled: block.enabled,
            surface,
          });
        }
        return false;
      }
      return true;
    });
    
    // Phase 2F: Skip placeholder blocks unless debug mode is on
    if (!debug) {
      return safeBlocks.filter(block => isBlockExtracted(block.type));
    }
    return safeBlocks;
  }, [spec, surface, debug, suppressBlockIds]);

  // Build render context (CR308/CR325: include components)
  const context: DealCardRenderContext = useMemo(() => ({
    isAdmin,
    surface,
    labels: spec.resolved.labels,
    styleOverrides: spec.resolved.styleOverrides,
    // CR308/CR325: Pass components config for v2 blocks (starburst, ribbon, etc.)
    components: spec.resolved.components,
  }), [isAdmin, surface, spec.resolved.labels, spec.resolved.styleOverrides, spec.resolved.components]);

  // CR327: Track remount for debug counters
  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackRenderEvent("v2", {
        dealType: spec.resolved.dealTypeKey,
        dealId: deal.id || deal.dedupe_key,
        surface,
      });
      hasTrackedRef.current = true;
      
      // CR327: Increment remount counter for stability debugging
      (async () => {
        try {
          const { incrementPreviewCounter } = await import("@/components/admin/deal-cards/PreviewStabilityCounters");
          incrementPreviewCounter("previewRemounts");
        } catch {
          // Counter module may not be loaded
        }
      })();
    }
  }, [spec.resolved.dealTypeKey, deal.id, deal.dedupe_key, surface]);

  // Debug fingerprint
  const fingerprint = debug ? computeSpecFingerprint(spec) : null;
  
  // CR305: Safe signature strip
  const showSig = shouldRenderDealCardSignatureStrip();
  const sigText = showSig ? buildDealCardSignature("v2", deal) : "";

  if (debug && isAdmin) {
    console.debug("[DealCardRendererV2]", deal.id || deal.dedupe_key, {
      surface,
      enabledBlocks: enabledBlocks.map(b => b.id),
      fingerprint,
      variantKey: spec.resolved.variantKey,
      dealTypeKey: spec.resolved.dealTypeKey,
    });
  }

  return (
    <div 
      className={cn("deal-card-v2 relative", `surface-${surface}`, className)}
      style={{
        // CR307: Consume theme CSS vars for card styling
        backgroundColor: "var(--wi-card-bg, hsl(0 0% 100%))",
        borderColor: "var(--wi-card-border, hsl(0 0% 0% / 0.05))",
        borderRadius: "var(--wi-card-radius, 24px)",
        boxShadow: "var(--wi-card-shadow, 0 10px 30px rgba(0,0,0,0.08))",
        padding: "var(--wi-card-padding, 1.5rem)",
        fontFamily: "var(--wi-body-font, system-ui, -apple-system, sans-serif)",
        color: "var(--wi-body-text, hsl(0 0% 10%))",
        borderWidth: "var(--wi-card-border-width, 1px)",
        borderStyle: "solid",
        ...style,
      }}
      data-deal-card-renderer="v2"
      data-spec-version="2"
      data-variant={spec.resolved.variantKey}
      data-deal-type={spec.resolved.dealTypeKey}
      data-surface={surface}
      data-deal-id={deal.id || "missing-id"}
      data-dedupe-key={deal.dedupe_key || ""}
    >
      {/* Debug header */}
      {debug && isAdmin && (
        <div className="text-xs bg-muted/50 text-muted-foreground px-2 py-1 rounded-t border-b border-border mb-2">
          <span className="font-mono">{fingerprint}</span>
        </div>
      )}
      
      {/* Render blocks */}
      <div className="space-y-2">
        {enabledBlocks.map((block) => (
          <BlockRenderer
            key={block.id}
            block={block}
            deal={deal}
            context={context}
            debug={debug}
          />
        ))}
      </div>
      
      {/* Empty state - only show if NO blocks rendered */}
      {enabledBlocks.length === 0 && (
        <div className="text-sm text-muted-foreground italic p-4 text-center">
          No blocks enabled for this surface
        </div>
      )}

      {/* CR443: Deal ID badge — visible on admin/dev surfaces */}
      {isAdmin && (
        <div className="mt-1 flex justify-end">
          <DealIdBadge dealId={deal.id} dedupeKey={deal.dedupe_key} />
        </div>
      )}
      
      {/* CR305: Debug render signature strip (safe iframe detection) */}
      {showSig && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-2 py-0.5 font-mono pointer-events-none z-50">
          {sigText}
        </div>
      )}
    </div>
  );
}

// ================================================================
// BLOCK RENDERER
// ================================================================

interface BlockRendererProps {
  block: DealCardBlockSpec;
  deal: Deal;
  context: DealCardRenderContext;
  debug?: boolean;
}

function BlockRenderer({ block, deal, context, debug }: BlockRendererProps) {
  const Component = getBlockComponent(block.type);
  const isExtracted = isBlockExtracted(block.type);
  
  // Phase 2F: In non-debug mode, don't render placeholder blocks at all
  if (!isExtracted && !debug) {
    return null;
  }
  
  if (debug) {
    return (
      <div 
        className="relative border border-dashed border-primary/30 rounded"
        data-block-debug={block.id}
      >
        <span className="absolute -top-2 left-2 text-[10px] bg-primary text-primary-foreground px-1 rounded">
          {block.id}{!isExtracted ? " (placeholder)" : ""}
        </span>
        <Component deal={deal} block={block} context={context} />
      </div>
    );
  }
  
  return <Component deal={deal} block={block} context={context} />;
}

// ================================================================
// EXPORTS
// ================================================================

export default DealCardRendererV2;
